import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LOGIN_URL } from "@/components/Navbar";
import { Cable, UserCircle, Building2, ArrowRight } from "lucide-react";

export default function Login() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const intent = params.get("role");

  useEffect(() => {
    if (!loading && user) navigate("/dashboard", { replace: true });
  }, [user, loading, navigate]);

  const login = (role) => {
    localStorage.setItem("intended_role", role);
    window.location.href = LOGIN_URL();
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-900 hero-grid px-4 py-12">
      <div className="max-w-3xl w-full animate-fade-up">
        <div className="flex items-center gap-2 mb-8 justify-center">
          <div className="w-10 h-10 rounded-lg copper-gradient flex items-center justify-center">
            <Cable className="w-6 h-6 text-white" />
          </div>
          <span className="font-display font-extrabold text-2xl text-white">Cable<span className="text-[#D96B27]">Careers</span></span>
        </div>
        <h1 className="font-display text-3xl font-bold text-center text-white mb-2">Sign in to CableCareers</h1>
        <p className="text-center text-slate-400 text-sm mb-10">Choose your account type — you'll be taken straight to your personalized dashboard.</p>

        <div className="grid md:grid-cols-2 gap-5">
          <button data-testid="candidate-login-card" onClick={() => login("candidate")}
            className={`group text-left bg-white rounded-2xl p-8 border-2 transition-all shadow-2xl hover:shadow-[#D96B27]/20 ${intent === "candidate" ? "border-[#D96B27]" : "border-transparent hover:border-[#D96B27]"}`}>
            <div className="w-14 h-14 rounded-xl bg-[#FFF3EC] flex items-center justify-center mb-5 group-hover:scale-105 transition-transform">
              <UserCircle className="w-8 h-8 text-[#D96B27]" />
            </div>
            <h3 className="font-display text-xl font-bold text-slate-900">Candidate Login</h3>
            <p className="text-sm text-slate-500 mt-2 mb-5">For engineers, technicians & managers in the cable & conductor industry. Build your profile, upload your resume, apply to jobs.</p>
            <span className="inline-flex items-center gap-2 text-sm font-semibold text-[#C05615]">
              <img src="https://www.google.com/favicon.ico" alt="" className="w-4 h-4" />
              Continue with Google <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </span>
          </button>

          <button data-testid="employer-login-card" onClick={() => login("employer")}
            className={`group text-left bg-white rounded-2xl p-8 border-2 transition-all shadow-2xl hover:shadow-[#D96B27]/20 ${intent === "employer" ? "border-[#D96B27]" : "border-transparent hover:border-[#D96B27]"}`}>
            <div className="w-14 h-14 rounded-xl bg-slate-100 flex items-center justify-center mb-5 group-hover:scale-105 transition-transform">
              <Building2 className="w-8 h-8 text-slate-700" />
            </div>
            <h3 className="font-display text-xl font-bold text-slate-900">Employer Login</h3>
            <p className="text-sm text-slate-500 mt-2 mb-5">For cable & conductor manufacturers and recruiters. Post jobs, search specialist talent, manage your hiring pipeline.</p>
            <span className="inline-flex items-center gap-2 text-sm font-semibold text-slate-800">
              <img src="https://www.google.com/favicon.ico" alt="" className="w-4 h-4" />
              Continue with Google <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </span>
          </button>
        </div>

        <p className="text-xs text-center text-slate-500 mt-8">New here? Signing in creates your account automatically with the role you choose. You can switch roles later from the navbar.</p>
      </div>
    </div>
  );
}
